import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaRocket, 
  FaMapMarkerAlt, 
  FaClock, 
  FaBriefcase, 
  FaEnvelope,
  FaExternalLinkAlt,
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle
} from 'react-icons/fa';
import SEO from '../components/SEO/SEO';
import contentData from '../data/contentData.json';
import { fadeIn, slideUp, staggerContainer } from '../utils/animations';

// Job Card Component
const JobCard = ({ job, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleApply = () => {
    window.open('https://dhyon.ezboss.in/forms/quote/67a4ed4149ee6c507fa423a6fc399aef?styled=1', '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100"
    >
      <div className="p-6">
        {/* Job Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2">{job.title}</h3>
            <p className="text-gray-600 text-sm mb-3">{job.description}</p>
          </div>
          <div className="flex space-x-2 ml-4">
            {job.type === 'Remote' && <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">🌐 Remote</span>}
            {job.type === 'Full-time' && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">💼 Full-time</span>}
            {job.type === 'Contract' && <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">🔥 Contract</span>}
            {job.type === 'Internship' && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">🎓 Internship</span>}
          </div>
        </div>

        {/* Job Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="flex items-center text-gray-600 text-sm">
            <FaMapMarkerAlt className="mr-2 text-primary" />
            {job.location}
          </div>
          <div className="flex items-center text-gray-600 text-sm">
            <FaBriefcase className="mr-2 text-primary" />
            {job.type}
          </div>
          <div className="flex items-center text-gray-600 text-sm">
            <FaClock className="mr-2 text-primary" />
            {job.experience}
          </div>
        </div>

        {/* Expandable Details */}
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t pt-4 mt-4"
          >
            {job.requirements && (
              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Requirements:</h4>
                <ul className="space-y-1">
                  {job.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start text-sm text-gray-600">
                      <FaCheckCircle className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {job.responsibilities && (
              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Responsibilities:</h4>
                <ul className="space-y-1">
                  {job.responsibilities.map((resp, idx) => (
                    <li key={idx} className="flex items-start text-sm text-gray-600">
                      <FaCheckCircle className="text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                      {resp}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-4 border-t">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center text-primary hover:text-primary-dark transition-colors text-sm font-medium"
          >
            {isExpanded ? 'Show Less' : 'View Details'}
            {isExpanded ? <FaChevronUp className="ml-1" /> : <FaChevronDown className="ml-1" />}
          </button>
          
          <button
            onClick={handleApply}
            className="bg-primary hover:bg-primary-dark text-white px-6 py-2 rounded-lg font-medium transition-all duration-300 hover:shadow-md flex items-center"
          >
            Apply Now
            <FaExternalLinkAlt className="ml-2 text-sm" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Culture Value Card Component
const CultureValueCard = ({ value, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
    className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100"
  >
    <div className="text-4xl mb-4">{value.icon}</div>
    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
    <p className="text-gray-600">{value.description}</p>
  </motion.div>
);

const CareersPage = () => {
  const careers = contentData.careers || [];
  const culture = contentData.company_culture || {};

  const handleSendResume = () => {
    window.open('https://dhyon.ezboss.in/forms/quote/67a4ed4149ee6c507fa423a6fc399aef?styled=1', '_blank');
  };

  return (
    <>
      <SEO
        title="Careers at Dhyon Technologies | Join Our Team"
        description="Explore job openings at Dhyon Technologies and help build the future of cloud, AI, and DevOps."
        keywords="careers, jobs, dhyon technologies, cloud engineer, devops, react developer, ahmedabad jobs"
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary to-primary-dark text-white py-24 overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="absolute top-10 right-10 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-10 left-10 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                variants={fadeIn}
                className="flex justify-center items-center mb-6"
              >
                <div className="bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 flex items-center space-x-2">
                  <span className="text-white font-medium">Now Hiring</span>
                  <FaRocket className="text-yellow-300 animate-bounce" />
                </div>
              </motion.div>

              <motion.h1
                variants={slideUp}
                className="text-4xl md:text-6xl font-bold mb-6"
              >
                Work With Us
              </motion.h1>
              
              <motion.p
                variants={slideUp}
                className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto"
              >
                Join a growing team building the future of cloud technologies
              </motion.p>

              <motion.div
                variants={fadeIn}
                className="text-center"
              >
                <div className="inline-block">
                  {/* Add a Lottie animation placeholder or SVG */}
                  <div className="w-48 h-48 mx-auto bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <div className="text-6xl">👥</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Why Join Us Section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Work at Dhyon?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
                {contentData.company.mission}
              </p>
            </motion.div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4 mb-16">
              {culture.values?.map((value, index) => (
                <CultureValueCard key={index} value={value} index={index} />
              ))}
            </div>

            {/* Company Perks */}
            {culture.perks && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-2xl p-8"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">What We Offer</h3>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  {culture.perks.map((perk, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                    >
                      <FaCheckCircle className="text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{perk}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </section>

        {/* Open Positions Section */}
        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Open Positions
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Discover exciting career opportunities and join our innovative team
              </p>
            </motion.div>

            <div className="grid gap-8 lg:grid-cols-2">
              {careers.map((job, index) => (
                <JobCard key={job.id} job={job} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Application CTA Section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="bg-gradient-to-r from-primary/10 to-blue-500/10 rounded-2xl p-12 border border-primary/20">
                <div className="text-6xl mb-6">💬</div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Don't see the right role?
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Send your resume anyway! We're always looking for talented individuals to join our team.
                </p>
                
                <button
                  onClick={handleSendResume}
                  className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-lg font-medium text-lg transition-all duration-300 hover:shadow-lg hover:scale-105 flex items-center mx-auto"
                >
                  <FaEnvelope className="mr-3" />
                  Send Resume
                  <FaExternalLinkAlt className="ml-3" />
                </button>

                <div className="mt-8 p-6 bg-blue-50 rounded-xl">
                  <p className="text-blue-800 font-medium">
                    💬 Want help applying? Ask us in chat or contact us directly!
                  </p>
                  <p className="text-blue-600 text-sm mt-2">
                    Email us at {contentData.company.contact.email} or call {contentData.company.contact.phone}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CareersPage; 